import type { TimbraturaPublic } from '@gestionale/shared';
import type { QueuedAction } from './offline-queue';

export function actionDay(timestamp: string): string {
	return new Intl.DateTimeFormat('en-CA', {
		timeZone: 'Europe/Rome',
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
	}).format(new Date(timestamp));
}

export function projectGiornata(
	server: TimbraturaPublic | null,
	actions: QueuedAction[],
	ownerId: string | null,
	day: string,
): TimbraturaPublic | null {
	let giornata =
		server?.data === day && server.dipendente_id === ownerId?.split(':')[1]
			? { ...server, sessioni: server.sessioni.map((s) => ({ ...s })) }
			: null;
	// Pending replays can already be reflected in a newer server snapshot.
	// Keep them in the queue, but never roll the displayed day back in time.
	const serverWatermark = Math.max(
		0,
		...[
			giornata?.ora_inizio,
			giornata?.ora_fine,
			...(giornata?.sessioni ?? []).flatMap((sessione) => [
				sessione.ora_ingresso,
				sessione.ora_uscita,
			]),
		]
			.map((time) => new Date(time ?? '').getTime())
			.filter(Number.isFinite),
	);
	for (const action of actions) {
		if (!ownerId || action.ownerId !== ownerId) continue;
		if (actionDay(action.payload.device_timestamp) !== day) continue;
		const time = action.payload.device_timestamp;
		if (new Date(time).getTime() < serverWatermark) continue;
		if (action.type === 'entrata' && !giornata) {
			giornata = {
				id: 'local:' + action.id,
				dipendente_id: ownerId.split(':')[1]!,
				data: day,
				ora_inizio: time,
				stato: 'aperta',
				pausa_minuti: 0,
				sessioni: [],
			};
		}
		if (!giornata) continue;
		if (action.type === 'riapriGiornata') {
			giornata.stato = 'aperta';
			giornata.ora_fine = undefined;
		}
		if (action.type === 'entraCantiere' || action.type === 'entraSessioneProvvisoria') {
			if (
				!giornata.sessioni.some(
					(sessione) =>
						!sessione.ora_uscita ||
						(new Date(sessione.ora_ingresso).getTime() === new Date(time).getTime() &&
							(action.type === 'entraCantiere'
								? sessione.cantiere_id === action.payload.cantiere_id
								: sessione.stato_associazione === 'da_associare')),
				)
			)
				giornata.sessioni.push({
					id: 'local:' + action.id,
					cantiere_id: action.payload.cantiere_id,
					stato_associazione: action.type === 'entraCantiere' ? 'associata' : 'da_associare',
					ora_ingresso: time,
				});
		}
		if (
			action.type === 'esciCantiere' ||
			action.type === 'esciSessioneProvvisoria' ||
			action.type === 'uscita'
		) {
			for (const sessione of giornata.sessioni) {
				if (
					!sessione.ora_uscita &&
					new Date(time).getTime() >= new Date(sessione.ora_ingresso).getTime() &&
					(action.type === 'uscita' ||
						(action.type === 'esciCantiere' &&
							sessione.cantiere_id === action.payload.cantiere_id) ||
						(action.type === 'esciSessioneProvvisoria' &&
							sessione.stato_associazione === 'da_associare'))
				) {
					sessione.ora_uscita = time;
					sessione.nota_lavoro = action.payload.nota_lavoro;
				}
			}
		}
		if (action.type === 'uscita') {
			giornata.stato = 'chiusa';
			giornata.ora_fine = time;
			giornata.pausa_minuti = action.payload.pausa_minuti ?? 0;
		}
	}
	return giornata;
}

const cacheKey = (owner: string, day: string) => 'giornata_cache:' + owner + ':' + day;
export function readGiornataCache(owner: string | null, day: string): TimbraturaPublic | null {
	if (!owner) return null;
	try {
		const value = JSON.parse(localStorage.getItem(cacheKey(owner, day)) ?? 'null');
		return value?.data === day && Array.isArray(value.sessioni) ? value : null;
	} catch {
		return null;
	}
}
export function cacheGiornata(
	owner: string | null,
	day: string,
	giornata: TimbraturaPublic | null,
) {
	if (owner) localStorage.setItem(cacheKey(owner, day), JSON.stringify(giornata));
}
